import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

const STATUS_COLOR = {
  occupied:    { bg: 'rgba(13,148,136,.15)',  text: '#0d9488' },
  vacant:      { bg: 'rgba(100,116,139,.12)', text: '#64748b' },
  cleaning:    { bg: 'rgba(245,158,11,.15)',  text: '#d97706' },
  maintenance: { bg: 'rgba(239,68,68,.15)',   text: '#ef4444' },
};

function StatCard({ label, value, sub, color }) {
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color: color || '#fff', lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function PMDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [turnovers, setTurnovers] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const week = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    Promise.all([
      api.get('/pm/stats'),
      api.get(`/pm/turnovers?status=scheduled&from=${today}&to=${week}`),
      api.get('/pm/work-orders?status=open'),
    ]).then(([s, t, w]) => {
      setStats(s.data);
      setTurnovers(t.data.slice(0, 5));
      setWorkOrders(w.data.slice(0, 5));
    }).catch(() => {});
  }, []);

  const u = stats?.units;

  return (
    <>
      <div className="page-header">
        <span className="page-title">Property Dashboard</span>
      </div>
      <div className="page-body">

        {/* Unit status bar */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Occupied',    value: u?.occupied,    color: '#0d9488' },
            { label: 'Vacant',      value: u?.vacant,      color: '#94a3b8' },
            { label: 'Cleaning',    value: u?.cleaning,    color: '#d97706' },
            { label: 'Maintenance', value: u?.maintenance, color: '#ef4444' },
          ].map(s => (
            <StatCard key={s.label} label={s.label} value={s.value} color={s.color}
              sub={`of ${u?.total ?? '?'} total units`} />
          ))}
        </div>

        {/* Alert row */}
        {stats && (stats.work_orders.urgent > 0 || stats.turnovers.upcoming > 0) && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            {stats.work_orders.urgent > 0 && (
              <div style={{ flex: 1, minWidth: 200, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#ef4444', fontWeight: 700 }}>🚨 {stats.work_orders.urgent} urgent work order{stats.work_orders.urgent !== 1 ? 's' : ''}</span>
                <button onClick={() => navigate('/property/work-orders')} style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View →</button>
              </div>
            )}
            {stats.turnovers.upcoming > 0 && (
              <div style={{ flex: 1, minWidth: 200, background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#d97706', fontWeight: 700 }}>🔄 {stats.turnovers.upcoming} turnover{stats.turnovers.upcoming !== 1 ? 's' : ''} this week</span>
                <button onClick={() => navigate('/property/turnovers')} style={{ fontSize: 12, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View →</button>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Upcoming turnovers */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>🔄 Upcoming Turnovers</span>
              <button onClick={() => navigate('/property/turnovers')} style={{ fontSize: 11, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer' }}>All →</button>
            </div>
            <div className="card-body" style={{ paddingTop: 8 }}>
              {turnovers.length === 0
                ? <div style={{ color: '#64748b', fontSize: 13 }}>No turnovers scheduled this week</div>
                : turnovers.map(t => (
                  <div key={t.id} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,.05)', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{t.property_name} · {t.unit_number}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        Checkout {t.checkout_date ? new Date(t.checkout_date + 'T12:00').toLocaleDateString('en', { month: 'short', day: 'numeric' }) : '—'}
                        {t.assigned_to && ` · ${t.assigned_to}`}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(245,158,11,.15)', color: '#d97706' }}>
                      {t.type}
                    </span>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Open work orders */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>🔧 Open Work Orders</span>
              <button onClick={() => navigate('/property/work-orders')} style={{ fontSize: 11, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer' }}>All →</button>
            </div>
            <div className="card-body" style={{ paddingTop: 8 }}>
              {workOrders.length === 0
                ? <div style={{ color: '#16a34a', fontSize: 13 }}>✓ No open work orders</div>
                : workOrders.map(w => (
                  <div key={w.id} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,.05)', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{w.title}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {w.property_name}{w.unit_number ? ` · ${w.unit_number}` : ''}
                        {w.assigned_to && ` · ${w.assigned_to}`}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                      background: w.priority === 'urgent' ? 'rgba(239,68,68,.15)' : w.priority === 'high' ? 'rgba(249,115,22,.15)' : 'rgba(100,116,139,.12)',
                      color: w.priority === 'urgent' ? '#ef4444' : w.priority === 'high' ? '#f97316' : '#94a3b8',
                    }}>
                      {w.priority}
                    </span>
                  </div>
                ))
              }
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 16 }}>
          {[
            { path: '/property/properties', icon: '🏠', label: 'Properties' },
            { path: '/property/work-orders', icon: '🔧', label: 'Work Orders' },
            { path: '/property/turnovers', icon: '🔄', label: 'Turnovers' },
            { path: '/property/owners', icon: '💰', label: 'Owners' },
          ].map(item => (
            <button key={item.path} onClick={() => navigate(item.path)}
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '16px 12px', cursor: 'pointer', textAlign: 'center' }}>
              <div style={{ fontSize: 26, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#cbd5e1' }}>{item.label}</div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
